import type { IssueListResponse, UnlinkedVoiceState } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useIngestWrite } from '../../hooks/useIssueIngest.js';
import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { formatIssueTime, ISSUE_UNLINKED_STATE_LABEL_KEYS } from '../../lib/issue-mgmt-format.js';
import { IssueText } from './IssueText.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 「振り分けに戻す」を出す声の状態（AI の除外は除外から戻す、課題ではない・旧表で処理済みは要点を振り分け待ちに戻す）。
 * @graph-connects none
 */
const RETURNABLE: ReadonlySet<UnlinkedVoiceState> = new Set([
  'excluded',
  'not_issue',
  'legacy_done',
]);

/**
 * 検索に当たった声のうち、課題に紐づいていないもの（旧表で処理済み・課題ではない・除外・振り分け待ち）。
 * 投稿日時・チャンネル（非公開の札）・本文の冒頭・状態・「Slackで開く」を出し、続きは「もっと見る」で出す。
 * 一度に出せる上限まで出したら、それ以上は増やさず検索語で絞るよう促す。
 * 編集キーの人は、除外・課題ではない・旧表で処理済みの声を「振り分けに戻す」でやることの振り分けカードにできる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題タブの検索で当たった、課題に紐づいていない声を表の下に見せ、自分の声がどう扱われたかを探せるようにする
 * @graph-connects IssueText [calls] 伏せ字の札と検索語の色
 * @graph-connects useIngestWrite [calls] restoreExcludedVoice
 * @graph-connects useIssueMgmtWrite [calls] returnVoiceToTriage
 */
export function UnlinkedVoices({
  unlinked,
  query,
  now,
  canEdit,
  onShowMore,
}: {
  unlinked: NonNullable<IssueListResponse['unlinkedVoices']>;
  query: string;
  now: Date;
  canEdit: boolean;
  /** 続きを出す。null なら一度に出せる上限まで出しているので、絞り込みを促す */
  onShowMore: (() => void) | null;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const restore = useIngestWrite('restoreExcludedVoice');
  const returnToTriage = useIssueMgmtWrite('returnVoiceToTriage');
  const busy = restore.isPending || returnToTriage.isPending;
  const back = (voiceId: string, state: UnlinkedVoiceState) => {
    const options = {
      onSuccess: (result: { operationId: string | null }) => {
        toast.notifySaved(t('issueMgmt.excluded.restored'), {
          operationId: result.operationId,
          detail: t('issueMgmt.excluded.restoredDetail'),
        });
      },
      onError: toast.notifyError,
    };
    if (state === 'excluded') restore.mutate({ voiceId }, options);
    else returnToTriage.mutate({ voiceId }, options);
  };
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
        {t('issueMgmt.list.unlinkedTitle', { n: String(unlinked.total) })}
      </h2>
      <ul className="divide-y divide-[var(--color-border)] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        {unlinked.items.map((voice) => (
          <li
            key={voice.voiceId}
            className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm"
          >
            <span className="w-40 shrink-0 text-xs text-[var(--color-text-muted)]">
              {formatIssueTime(voice.postedAt, voice.postedDate, now) ?? t('issueMgmt.common.dash')}{' '}
              {voice.channelName === null ? '' : `#${voice.channelName}`}
              {voice.isPrivate && (
                <span className="ml-1" title={t('issueMgmt.common.privateHint')}>
                  <Badge size="sm">{t('issueMgmt.common.private')}</Badge>
                </span>
              )}
            </span>
            <span className="min-w-0 flex-1 truncate text-[var(--color-text-primary)]">
              <IssueText text={voice.text.split('\n')[0] ?? ''} query={query} />
            </span>
            <Badge>{t(ISSUE_UNLINKED_STATE_LABEL_KEYS[voice.state])}</Badge>
            {canEdit && RETURNABLE.has(voice.state) && (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  back(voice.voiceId, voice.state);
                }}
              >
                {t('issueMgmt.excluded.restore')}
              </Button>
            )}
            {voice.slackUrl !== null && (
              <a
                href={voice.slackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[var(--color-accent-primary)] hover:underline"
              >
                {t('issueMgmt.common.openSlack')}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </li>
        ))}
      </ul>
      {unlinked.items.length < unlinked.total &&
        (onShowMore === null ? (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('issueMgmt.list.unlinkedNarrow', { n: String(unlinked.items.length) })}
          </p>
        ) : (
          <button
            type="button"
            onClick={onShowMore}
            className="mt-2 text-xs text-[var(--color-accent-primary)] hover:underline"
          >
            {t('issueMgmt.common.showMore')}
          </button>
        ))}
    </section>
  );
}
